import { Component, computed, input, OnDestroy, OnInit, signal } from '@angular/core';

import { InfoscreenFeedMessage } from '../infoscreen-module.types';

const SECONDS_PER_MESSAGE = 8;

/**
 * The free messages, on a fixed line above the bookings so they cannot scroll out of sight.
 * Several messages take turns; an important one is drawn in the warning colour.
 */
@Component({
  selector: 'app-message-bar',
  template: `
    @if (current(); as message) {
      <div class="bar" [class.important]="message.severity === 'Important'">
        <svg class="icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M11 15h2v2h-2zm0-8h2v6h-2zm1-5C6.47 2 2 6.5 2 12a10 10 0 1 0 10-10zm0 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16z" />
        </svg>
        <span class="text">{{ message.text }}</span>
        @if (messages().length > 1) {
          <span class="count">{{ index() % messages().length + 1 }}/{{ messages().length }}</span>
        }
      </div>
    }
  `,
  styles: `
    // Solid colours that read on every theme (WCAG AA): white on dark teal (5.4:1), and an important
    // message in the design system's warning orange #FFC999 with dark brown text (10:1). The old
    // white on #32BAD4 / #F2994A was 2.3:1 / 2.2:1.
    .bar {
      display: flex;
      align-items: center;
      gap: var(--cell-gap-icon);
      padding: calc(var(--cell-pad-tb) / 2) var(--gutter-size);
      background: #0e7487;
      color: #fff;
      font-size: var(--fontsize-medium);
      font-weight: var(--fontweight-medium);
      line-height: 1.2;

      &.important {
        background: #ffc999;
        color: #3d1f00;
      }
    }
    .icon {
      flex-shrink: 0;
      width: var(--icon-size);
      height: var(--icon-size);
      fill: currentColor;
    }
    .text {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .count {
      font-size: var(--fontsize-small);
      opacity: 0.8;
    }
  `,
})
export class MessageBarComponent implements OnInit, OnDestroy {
  readonly messages = input.required<InfoscreenFeedMessage[]>();

  readonly index = signal(0);
  readonly current = computed(() => {
    const all = this.messages();
    return all.length ? all[this.index() % all.length] : null;
  });

  private intervalId?: ReturnType<typeof setInterval>;

  ngOnInit() {
    this.intervalId = setInterval(() => this.index.update((i) => i + 1), SECONDS_PER_MESSAGE * 1000);
  }

  ngOnDestroy() {
    clearInterval(this.intervalId);
  }
}
